/**
 * Centralised badge → image mapping.
 *
 * Keys are the badge IDs as they exist in code (`src/lib/badges.ts`) and the
 * `badge_definitions` table in Supabase. The Romanian aliases (e.g.
 * `tricolorul`, `regele-carpatilor`) are also wired here so the config keeps
 * working if/when the DB IDs are renamed to match the badge names.
 *
 * Image files live in `public/badges/`. Filenames contain spaces and are
 * URL-encoded so the browser can fetch them directly.
 *
 * Sources ship as both `.webp` (preferred) and `.png` (legacy fallback).
 */

const f = (name: string) => `/badges/${encodeURI(name)}.webp`;

/** Returns the `.png` fallback URL for a given `.webp` badge path. */
export function badgePngFallback(path: string): string {
  return path.replace(/\.webp$/i, ".png");
}

const IMAGES = {
  tricolorul: f("Tricolorul"),
  generatiaDeAur: f("Goldern Generation"), // file ships with this spelling
  miorita: f("Little Ewe"),
  regeleCarpatilor: f("King of Carpathians"),
  balaurul: f("The Dragon"),
  driblingDeHagi: f("Hagis Nutmeg"),
  maradonaDinCarpati: f("Carpathian Maradona"),
  analistul: f("The Analyst"),
  lunetistul: f("The Sniper"),
  ursulCarpatin: f("Carpathian Bear"),
  muscaturaLuiDracula: f("Dracula"),
  fortaPalincii: f("Palinca"),
  arenaNationala: f("National Arena"),
  ghicitorul: f("The Seer"),
  zeceDinZece: f("Ten out of ten"),
  dumnezeulFotbalului: f("God"),
  oracolul: f("Oracle"),
  modulDumnezeu: f("God Mode"),
} as const;

export const BADGE_IMAGES: Record<string, string> = {
  // Current code/DB IDs
  "canarinho": IMAGES.tricolorul,
  "samba-streak": IMAGES.generatiaDeAur,
  "mioritza": IMAGES.miorita,
  "rei-do-placar": IMAGES.regeleCarpatilor,
  "balaurul": IMAGES.balaurul,
  "caneta": IMAGES.driblingDeHagi,
  "pele-mode": IMAGES.maradonaDinCarpati,
  "analistul": IMAGES.analistul,
  "bicudo": IMAGES.lunetistul,
  "ursul-carpatin": IMAGES.ursulCarpatin,
  "dracula": IMAGES.muscaturaLuiDracula,
  "palinka-power": IMAGES.fortaPalincii,
  "maracana": IMAGES.arenaNationala,
  "ghicitorul": IMAGES.ghicitorul,
  "zece-din-zece": IMAGES.zeceDinZece,
  "dumnezeul-fotbalului": IMAGES.dumnezeulFotbalului,
  "oracolul": IMAGES.oracolul,
  "modul-dumnezeu": IMAGES.modulDumnezeu,

  // Romanian aliases (forward-compat with the renamed-ID plan)
  "tricolorul": IMAGES.tricolorul,
  "generatia-de-aur": IMAGES.generatiaDeAur,
  "miorita": IMAGES.miorita,
  "regele-carpatilor": IMAGES.regeleCarpatilor,
  "dribling-de-hagi": IMAGES.driblingDeHagi,
  "maradona-din-carpati": IMAGES.maradonaDinCarpati,
  "lunetistul": IMAGES.lunetistul,
  "muscatura-lui-dracula": IMAGES.muscaturaLuiDracula,
  "forta-palincii": IMAGES.fortaPalincii,
  "arena-nationala": IMAGES.arenaNationala,
};

export function getBadgeImage(badgeId: string): string | null {
  return BADGE_IMAGES[badgeId] ?? null;
}

export function hasBadgeImage(badgeId: string): boolean {
  return badgeId in BADGE_IMAGES;
}

/**
 * Warm the browser cache with every badge image (both webp + png fallback)
 * so the Profile / Notifications screens render instantly. Idempotent —
 * safe to call multiple times; only runs in the browser.
 */
let _preloaded = false;
export function preloadBadgeImages(): void {
  if (_preloaded || typeof window === "undefined") return;
  _preloaded = true;
  const supportsWebp =
    typeof document !== "undefined" &&
    document.createElement("canvas").toDataURL("image/webp").startsWith("data:image/webp");
  const urls = Object.values(IMAGES);
  for (const url of urls) {
    const img = new Image();
    img.decoding = "async";
    img.src = supportsWebp ? url : badgePngFallback(url);
  }
}
